import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, useTheme, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { 
  Sun, 
  Moon, 
  LogOut, 
  ArrowLeft,
  Users,
  BookOpen,
  Download,
  RefreshCw,
  Shield,
  Mail,
  Calendar,
  Trash2,
  RotateCcw,
  MessageSquare
} from "lucide-react";
import { KITT } from "@/constants/hedgekit";
import { toast } from "sonner";

// Admin email - only this user can access the dashboard
const ADMIN_EMAIL = "shepromptsja@gmail.com";

export default function Admin() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0 });
  const [feedback, setFeedback] = useState([]);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, user: null });
  const [activeTab, setActiveTab] = useState('users');

  // Check if current user is admin
  const isAdmin = user?.email === ADMIN_EMAIL;

  // Helper to get auth headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('kbn_session_token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  useEffect(() => {
    if (isAdmin) {
      fetchUsers();
      fetchFeedback();
    }
  }, [isAdmin]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/admin/users`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
        setStats({ total: data.total_users || 0 });
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFeedback = async () => {
    try {
      const response = await fetch(`${API}/feedback/list`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setFeedback(data || []);
      }
    } catch (error) {
      console.error('Failed to fetch feedback:', error);
    }
  };

  const deleteUser = async (userId, email) => {
    if (!userId) {
      toast.error('Invalid user ID');
      setDeleteDialog({ open: false, user: null });
      return;
    }
    try {
      const response = await fetch(`${API}/admin/users/${userId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        toast.success(`Deleted user: ${email}`);
        fetchUsers();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to delete user');
      }
    } catch (error) {
      toast.error('Failed to delete user');
    }
    setDeleteDialog({ open: false, user: null });
  };

  const resetUserProgress = async (userId, email) => {
    if (!userId) {
      toast.error('Invalid user ID');
      return;
    }
    try {
      const response = await fetch(`${API}/admin/users/${userId}/reset`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        toast.success(`Reset progress for: ${email}`);
      } else {
        toast.error('Failed to reset progress');
      }
    } catch (error) {
      toast.error('Failed to reset progress');
    }
  };

  const exportCSV = async () => {
    try {
      const response = await fetch(`${API}/admin/users/export`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        // Create and download CSV file
        const blob = new Blob([data.csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `storybands_users_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Failed to export:', error);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Not admin - show access denied
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center p-8">
          <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-heading font-bold mb-2">Access Denied</h1>
          <p className="text-muted-foreground mb-6">You don't have permission to view this page.</p>
          <Button onClick={() => navigate('/home')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Go Home
          </Button>
        </div>
      </div>
    );
  }

  const formatDate = (dateStr) => {
    if (!dateStr || dateStr === "Unknown") return "Unknown";
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/home')}
              className="rounded-full"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-primary" />
              <span className="font-heading font-bold text-xl">Admin Dashboard</span>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="rounded-full"
            >
              {theme === 'light' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="rounded-full p-1">
                  <Avatar className="w-9 h-9">
                    <AvatarImage src={user?.picture} alt={user?.name} />
                    <AvatarFallback className="bg-primary/10 text-primary font-bold">
                      {user?.name?.charAt(0) || 'A'}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-3 py-2">
                  <p className="font-medium">{user?.name}</p>
                  <p className="text-sm text-muted-foreground truncate">{user?.email}</p>
                  <p className="text-xs text-primary mt-1">Admin</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout}>
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <div className="bg-gradient-to-br from-blue-100 to-blue-50 dark:from-blue-950/50 dark:to-blue-900/30 rounded-2xl p-6 border border-blue-200 dark:border-blue-800">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-3xl font-heading font-bold">{stats.total}</p>
                <p className="text-sm text-muted-foreground">Total Users</p>
              </div>
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-green-100 to-green-50 dark:from-green-950/50 dark:to-green-900/30 rounded-2xl p-6 border border-green-200 dark:border-green-800">
            <div className="flex items-center gap-4">
              <img src={KITT.thumbsUp} alt="Kitt" className="w-12 h-12 object-contain" />
              <div>
                <p className="text-3xl font-heading font-bold">Kitt's Book Nook</p>
                <p className="text-sm text-muted-foreground">App Status: Live</p>
              </div>
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-amber-100 to-amber-50 dark:from-amber-950/50 dark:to-amber-900/30 rounded-2xl p-6 border border-amber-200 dark:border-amber-800">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-amber-500 rounded-xl flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-3xl font-heading font-bold">24</p>
                <p className="text-sm text-muted-foreground">Achievements</p>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex gap-2">
            <Button 
              variant={activeTab === 'users' ? 'default' : 'outline'} 
              size="sm"
              onClick={() => setActiveTab('users')}
            >
              <Users className="w-4 h-4 mr-2" />
              Users ({stats.total})
            </Button>
            <Button 
              variant={activeTab === 'feedback' ? 'default' : 'outline'} 
              size="sm"
              onClick={() => setActiveTab('feedback')}
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              Feedback ({feedback.length})
            </Button>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => { fetchUsers(); fetchFeedback(); }} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button variant="outline" size="sm" onClick={exportCSV}>
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </div>

        {/* Users Table */}
        {activeTab === 'users' && (
        <div className="bg-card border border-border rounded-2xl overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <img 
                src={KITT.reading} 
                alt="Loading" 
                className="w-20 h-20 object-contain mx-auto mb-4 animate-pulse"
              />
              <p className="text-muted-foreground">Loading users...</p>
            </div>
          ) : users.length === 0 ? (
            <div className="p-12 text-center">
              <Users className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
              <p className="text-muted-foreground">No users yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left px-6 py-4 text-sm font-medium text-muted-foreground">User</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-muted-foreground">Email</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-muted-foreground">Signed Up</th>
                    <th className="text-right px-6 py-4 text-sm font-medium text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {users.map((u, idx) => (
                    <tr key={idx} className="hover:bg-muted/30 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <Avatar className="w-10 h-10">
                            <AvatarImage src={u.picture} alt={u.name} />
                            <AvatarFallback className="bg-primary/10 text-primary font-bold">
                              {u.name?.charAt(0) || '?'}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium">{u.name || 'Unknown'}</p>
                            {u.email === ADMIN_EMAIL && (
                              <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">Admin</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2 text-sm">
                          <Mail className="w-4 h-4 text-muted-foreground" />
                          <a href={`mailto:${u.email}`} className="hover:text-primary transition-colors">
                            {u.email}
                          </a>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Calendar className="w-4 h-4" />
                          {formatDate(u.created_at)}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => resetUserProgress(u.user_id, u.email)}
                            title="Reset progress"
                          >
                            <RotateCcw className="w-4 h-4" />
                          </Button>
                          {u.email !== ADMIN_EMAIL && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-500 hover:text-red-600 hover:bg-red-50"
                              onClick={() => setDeleteDialog({ open: true, user: u })}
                              title="Delete user"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        )}

        {/* Feedback Tab */}
        {activeTab === 'feedback' && (
          <div className="bg-card border border-border rounded-2xl overflow-hidden">
            {feedback.length === 0 ? (
              <div className="p-12 text-center">
                <MessageSquare className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
                <p className="text-muted-foreground">No feedback yet</p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {feedback.map((f, idx) => (
                  <div key={idx} className="p-6">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                          f.category === 'bug' ? 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300' :
                          f.category === 'feature' ? 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300' :
                          'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300'
                        }`}>
                          {f.category}
                        </span>
                        <a href={`mailto:${f.email}`} className="text-sm text-muted-foreground hover:text-primary">
                          {f.email}
                        </a>
                      </div>
                      <span className="text-xs text-muted-foreground">{formatDate(f.created_at)}</span>
                    </div>
                    <p className="text-sm">{f.message}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Quick Tips */}
        <div className="mt-8 bg-muted/30 rounded-2xl p-6 border border-border">
          <h3 className="font-heading font-bold mb-3">Quick Tips</h3>
          <ul className="text-sm text-muted-foreground space-y-2">
            <li>• <strong>Reset Progress:</strong> Clears a user's reading progress and achievements</li>
            <li>• <strong>Delete User:</strong> Permanently removes user and all their data</li>
            <li>• <strong>Export CSV:</strong> Download user emails for newsletters</li>
            <li>• <strong>Security:</strong> Only you ({ADMIN_EMAIL}) can access this page</li>
          </ul>
        </div>
      </main>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, user: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete <strong>{deleteDialog.user?.email}</strong> and all their data including reading progress, achievements, and session. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-500 hover:bg-red-600"
              onClick={() => deleteUser(deleteDialog.user?.user_id, deleteDialog.user?.email)}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
